// First Created - 04 Sep 2026
// Author - Atul Pandey
// Purpose - RTK Query API slice for Trinity Intention Board responses.
//           Supports private response viewing and public candidate
//           access, draft save, and final submission.
// Updated - 13 Sep 2026 - addded endpoint for response insights
// Updated - 27 Sep 2026 - addded endpoint for my intention responses

import {
  createSelector,
  createEntityAdapter,
} from '@reduxjs/toolkit'

import {
  apiSlice,
} from '@/app/api/apiSlice'

/*
|--------------------------------------------------------------------------
| Entity Adapter
|--------------------------------------------------------------------------
*/

const orgIntentionResponsesAdapter =
  createEntityAdapter({})

const initialState =
  orgIntentionResponsesAdapter
    .getInitialState()

/*
|--------------------------------------------------------------------------
| API Slice
|--------------------------------------------------------------------------
*/

export const orgIntentionResponsesApiSlice =
  apiSlice.injectEndpoints({

    endpoints:
      builder => ({

        
        /*
        |--------------------------------------------------------------------------
        | Get My Intention Responses - 27 Sep 2026
        |--------------------------------------------------------------------------
        |
        | Personal authenticated Trinity User endpoint.
        |
        | No userId, candidateId, orgId or intentionBoardId supplied.
        |
        | The backend resolves the user's identity through:
        |
        | loggedInUser._id
        |   -> OrgIntentionCandidateOnboarding.userId
        |
        */

        getMyOrgIntentionResponses:
          builder.query({

            query:
              () => ({

                url:
                  '/orgIntentionResponse/getMyOrgIntentionResponses',

                method:
                  'GET',

              }),


            transformResponse:
              responseData => {

                const journeys =
                  Array.isArray(
                    responseData?.journeys
                  )
                    ? responseData.journeys
                    : []


                return {

                  exists:
                    Boolean(
                      responseData?.exists
                    ),

                  journeys,

                }

              },


            providesTags: [
              {
                type:
                  'OrgIntentionResponse',

                id:
                  'MY-INTENTION',
              },
            ],


            refetchOnMountOrArgChange:
              true,

          }),
        
        
        /*
        |--------------------------------------------------------------------------
        | Get My Peer Intention Responses - 27 Sep 2026
        |--------------------------------------------------------------------------
        */

        getMyPeerIntentionResponses:
          builder.query({

            query:
              ({
                intentionBoardId,
              }) => ({

                url:
                  '/orgIntentionResponse/getMyPeerIntentionResponses',

                params: {
                  intentionBoardId,
                },

              }),

            providesTags:
              (
                result,
                error,
                arg
              ) => [

                {
                  type: 'OrgIntentionResponse',
                  id: `PEER-${arg.intentionBoardId}`,
                },

              ],

            refetchOnMountOrArgChange:
              true,

          }),


        /*
        |--------------------------------------------------------------------------
        | Update My Sharing Preference - 27 Sep 2026
        |--------------------------------------------------------------------------
        */

        updateMyResponseSharing:
          builder.mutation({

            query:
              ({
                intentionBoardId,
                visibility,
              }) => ({

                url:
                  '/orgIntentionResponse/updateMyResponseSharing',

                method:
                  'PATCH',

                body: {
                  intentionBoardId,
                  visibility,
                },

              }),

            invalidatesTags:
              (
                result,
                error,
                arg
              ) => [

                {
                  type: 'OrgIntentionResponse',
                  id: `PEER-${arg.intentionBoardId}`,
                },

              ],

          }),

        /*
        |--------------------------------------------------------------------------
        | Get Intention Responses
        |--------------------------------------------------------------------------
        |
        | Private
        |
        | By default, backend returns submitted responses only.
        |
        */

        getOrgIntentionResponses:
          builder.query({

            query:
              ({
                intentionBoardId,
                responseStatus,
              }) => ({

                url:
                  '/orgIntentionResponse/getOrgIntentionResponses',

                params: {

                  intentionBoardId,

                  ...(responseStatus
                    ? {
                        responseStatus,
                      }
                    : {}),

                },

                validateStatus:
                  (
                    response,
                    result
                  ) => {

                    return (
                      response.status ===
                        200 &&
                      !result.isError
                    )

                  },

              }),

            transformResponse:
              responseData => {

                const loadedOrgIntentionResponses =
                  responseData.map(
                    intentionResponse => {

                      intentionResponse.id =
                        intentionResponse._id

                      return intentionResponse

                    }
                  )

                return orgIntentionResponsesAdapter
                  .setAll(
                    initialState,
                    loadedOrgIntentionResponses
                  )

              },

            providesTags:
              (
                result,
                error,
                arg
              ) => {

                if (
                  result?.ids
                ) {

                  return [

                    {
                      type:
                        'OrgIntentionResponse',

                      id:
                        `LIST-${arg?.intentionBoardId}`,
                    },

                    ...result.ids.map(
                      id => ({

                        type:
                          'OrgIntentionResponse',

                        id,

                      })
                    ),

                  ]

                }

                return [

                  {
                    type:
                      'OrgIntentionResponse',

                    id:
                      `LIST-${arg?.intentionBoardId}`,
                  },

                ]

              },

          }),

        /*
        |--------------------------------------------------------------------------
        | Get Response By Candidate
        |--------------------------------------------------------------------------
        |
        | Private
        |
        */

        getOrgIntentionResponseByCandidate:
          builder.query({

            query:
              ({
                intentionBoardId,
                intentionCandidateId,
              }) => ({

                url:
                  '/orgIntentionResponse/getOrgIntentionResponseByCandidate',

                params: {
                  intentionBoardId,
                  intentionCandidateId,
                },

                validateStatus:
                  (
                    response,
                    result
                  ) => {

                    return (
                      response.status ===
                        200 &&
                      !result.isError
                    )

                  },

              }),

            providesTags:
              (
                result,
                error,
                arg
              ) => [

                {
                  type:
                    'OrgIntentionResponse',

                  id:
                    result?._id ||
                    `CANDIDATE-${arg?.intentionCandidateId}`,
                },

              ],

          }),

        /*
        |--------------------------------------------------------------------------
        | Verify Public Intention Access
        |--------------------------------------------------------------------------
        |
        | Public
        |
        | Step 1 of candidate workflow.
        |
        | Validates:
        |
        | publicSlug
        | orgCode
        | passcode
        | candidateName
        | candidateEmail
        |
        | Returns intentionAccessToken.
        |
        */

        verifyOrgIntentionPublicAccess:
          builder.mutation({

            query:
              initialVerification => ({

                url:
                  '/orgIntentionResponse/verifyOrgIntentionPublicAccess',

                method:
                  'POST',

                body: {
                  ...initialVerification,
                },

              }),

          }),

        /*
        |--------------------------------------------------------------------------
        | Get Public Intention Response
        |--------------------------------------------------------------------------
        |
        | Public candidate token
        |
        | Returns:
        |
        | board
        | candidate
        | questions
        | response
        | responseWindowOpen
        | canEdit
        |
        */

        getPublicOrgIntentionResponse:
          builder.query({

            query:
              ({
                intentionAccessToken,
              }) => ({

                url:
                  '/orgIntentionResponse/getPublicOrgIntentionResponse',

                headers: {

                  Authorization:
                    `Bearer ${intentionAccessToken}`,

                },

                validateStatus:
                  (
                    response,
                    result
                  ) => {

                    return (
                      response.status ===
                        200 &&
                      !result.isError
                    )

                  },

              }),

            /*
            |--------------------------------------------------------------------------
            | No Normal RTK Tags Required
            |--------------------------------------------------------------------------
            |
            | This is public-session state and we will explicitly refetch or
            | invalidate it after save / submit.
            |
            */

            providesTags:
              (
                result,
                error,
                arg
              ) => [

                {
                  type:
                    'OrgIntentionResponse',

                  id:
                    result?.candidate
                      ?.intentionCandidateId
                      ? `PUBLIC-${result.candidate.intentionCandidateId}`
                      : 'PUBLIC',
                },

              ],

          }),

        /*
        |--------------------------------------------------------------------------
        | Save Public Intention Response
        |--------------------------------------------------------------------------
        |
        | Public candidate token
        |
        */

        savePublicOrgIntentionResponse:
          builder.mutation({

            query:
              ({
                intentionAccessToken,
                answers,
              }) => ({

                url:
                  '/orgIntentionResponse/savePublicOrgIntentionResponse',

                method:
                  'PATCH',

                headers: {

                  Authorization:
                    `Bearer ${intentionAccessToken}`,

                },

                body: {
                  answers,
                },

              }),

            invalidatesTags:
              (
                result,
                error,
                arg
              ) => {

                const candidateId =
                  result?.response
                    ?.candidateId

                return [

                  {
                    type:
                      'OrgIntentionResponse',

                    id:
                      candidateId
                        ? `PUBLIC-${candidateId}`
                        : 'PUBLIC',
                  },

                  /*
                  |--------------------------------------------------------------------------
                  | Candidate Lifecycle Changed
                  |--------------------------------------------------------------------------
                  |
                  | Saving a response may move:
                  |
                  | accessed -> draft
                  |
                  */

                  ...(candidateId
                    ? [
                        {
                          type:
                            'OrgIntentionCandidate',

                          id:
                            candidateId,
                        },
                      ]
                    : []),

                ]

              },

          }),

        /*
        |--------------------------------------------------------------------------
        | Submit Public Intention Response
        |--------------------------------------------------------------------------
        |
        | Public candidate token
        |
        | This transition is irreversible.
        |
        */

        submitPublicOrgIntentionResponse:
          builder.mutation({

            query:
              ({
                intentionAccessToken,
              }) => ({

                url:
                  '/orgIntentionResponse/submitPublicOrgIntentionResponse',

                method:
                  'PATCH',

                headers: {

                  Authorization:
                    `Bearer ${intentionAccessToken}`,

                },

                body: {},

              }),

            invalidatesTags:
              (
                result,
                error,
                arg
              ) => {

                const candidateId =
                  result?.response
                    ?.candidateId

                const intentionBoardId =
                  result?.response
                    ?.intentionBoardId

                const responseId =
                  result?.response
                    ?._id

                return [

                  {
                    type:
                      'OrgIntentionResponse',

                    id:
                      candidateId
                        ? `PUBLIC-${candidateId}`
                        : 'PUBLIC',
                  },

                  ...(responseId
                    ? [
                        {
                          type:
                            'OrgIntentionResponse',

                          id:
                            responseId,
                        },
                      ]
                    : []),

                  ...(intentionBoardId
                    ? [
                        {
                          type:
                            'OrgIntentionResponse',

                          id:
                            `LIST-${intentionBoardId}`,
                        },

                        {
                          type:
                            'OrgIntentionCandidate',

                          id:
                            `LIST-${intentionBoardId}`,
                        },
                      ]
                    : []),

                  ...(candidateId
                    ? [
                        {
                          type:
                            'OrgIntentionCandidate',

                          id:
                            candidateId,
                        },
                      ]
                    : []),

                ]

              },

          }),
        
        // 06 Sep 2026
        /*
        |--------------------------------------------------------------------------
        | Get Intention Board Response Summary
        |--------------------------------------------------------------------------
        */

        getOrgIntentionResponseSummary:
            builder.query({

                query:
                ({
                    intentionBoardId,
                }) => ({

                    url:
                    '/orgIntentionResponse/getOrgIntentionResponseSummary',

                    method:
                    'GET',

                    params: {

                    intentionBoardId,

                    },

                }),

                providesTags:
                (
                    result,
                    error,
                    {
                    intentionBoardId,
                    }
                ) => [

                    {
                    type:
                        'OrgIntentionResponse',

                    id:
                        `SUMMARY-${intentionBoardId}`,
                    },

                ],

            }),

        /*
        |--------------------------------------------------------------------------
        | Get Submitted Response By Candidate
        |--------------------------------------------------------------------------
        */

        getOrgIntentionResponseByCandidate:
            builder.query({

                query:
                ({
                    intentionBoardId,
                    intentionCandidateId,
                }) => ({

                    url:
                    '/orgIntentionResponse/getOrgIntentionResponseByCandidate',

                    method:
                    'GET',

                    params: {

                    intentionBoardId,

                    intentionCandidateId,

                    },

                }),

                providesTags:
                (
                    result,
                    error,
                    {
                    intentionCandidateId,
                    }
                ) => [

                    {
                    type:
                        'OrgIntentionResponse',

                    id:
                        intentionCandidateId,
                    },

                ],

            }),

        /*
        |--------------------------------------------------------------------------
        | Get Intention Board Response Insights - 13 Sep 2026
        |--------------------------------------------------------------------------
        |
        | Private
        |
        | Aggregated response intelligence for one Intention Board.
        |
        */

        getOrgIntentionResponseInsights:
          builder.query({

            query:
              ({
                intentionBoardId,
              }) => ({

                url:
                  '/orgIntentionResponse/getOrgIntentionResponseInsights',

                params: {
                  intentionBoardId,
                },

              }),

            providesTags:
              (
                result,
                error,
                arg
              ) => [

                {
                  type:
                    'OrgIntentionResponse',

                  id:
                    `INSIGHTS-${arg?.intentionBoardId}`,
                },

              ],

          }),

      }),

  })

/*
|--------------------------------------------------------------------------
| Hooks
|--------------------------------------------------------------------------
*/

export const {

  
  /*
  |--------------------------------------------------------------------------
  | My Intention - 27 Sep 2026
  |--------------------------------------------------------------------------
  */

  useGetMyOrgIntentionResponsesQuery,

  useLazyGetMyOrgIntentionResponsesQuery,

  useGetMyPeerIntentionResponsesQuery,

  useLazyGetMyPeerIntentionResponsesQuery,
  
  useUpdateMyResponseSharingMutation,

  /*
  |--------------------------------------------------------------------------
  | Private
  |--------------------------------------------------------------------------
  */

  useGetOrgIntentionResponsesQuery,

  useGetOrgIntentionResponseByCandidateQuery,

  // 06 Sep 2026
  useLazyGetOrgIntentionResponseByCandidateQuery, 

  // 13 Sep 2026
  useGetOrgIntentionResponseInsightsQuery,

  /*
  |--------------------------------------------------------------------------
  | Public
  |--------------------------------------------------------------------------
  */

  useVerifyOrgIntentionPublicAccessMutation,

  useGetPublicOrgIntentionResponseQuery,

  useLazyGetPublicOrgIntentionResponseQuery,

  useSavePublicOrgIntentionResponseMutation,

  useSubmitPublicOrgIntentionResponseMutation,

  // 06 Sep 2026
  useGetOrgIntentionResponseSummaryQuery,

  useLazyGetOrgIntentionResponseSummaryQuery,


} =
  orgIntentionResponsesApiSlice

/*
|--------------------------------------------------------------------------
| Private Response Query Result Selector
|--------------------------------------------------------------------------
*/

export const selectOrgIntentionResponsesResult =
  (
    intentionBoardId,
    responseStatus
  ) =>
    orgIntentionResponsesApiSlice
      .endpoints
      .getOrgIntentionResponses
      .select({

        intentionBoardId,

        ...(responseStatus
          ? {
              responseStatus,
            }
          : {}),

      })

/*
|--------------------------------------------------------------------------
| Private Response Data Selector
|--------------------------------------------------------------------------
*/

export const selectOrgIntentionResponsesData =
  (
    intentionBoardId,
    responseStatus
  ) =>
    createSelector(

      selectOrgIntentionResponsesResult(
        intentionBoardId,
        responseStatus
      ),

      orgIntentionResponsesResult =>
        orgIntentionResponsesResult
          .data

    )

/*
|--------------------------------------------------------------------------
| Entity Selectors
|--------------------------------------------------------------------------
*/

export const selectOrgIntentionResponseSelectors =
  (
    intentionBoardId,
    responseStatus
  ) =>
    orgIntentionResponsesAdapter
      .getSelectors(

        state => {

          const responseData =
            selectOrgIntentionResponsesData(
              intentionBoardId,
              responseStatus
            )(
              state
            )

          return (
            responseData ??
            initialState
          )

        }

      )